import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import useTheme from "components/ui/useTheme";
import type { CSSProperties } from "react";
import { useIntl } from "react-intl";

interface IndicatorsButtonArrayProps {
  className?: string;
  selectedIndicators: IIndicatorFieldsFragment[];
  indicators: IIndicatorFieldsFragment[];
  onToggleIndicator: (indicator: IIndicatorFieldsFragment, isSelected: boolean) => void;
}
export default function IndicatorsButtonArray({
  className,
  selectedIndicators,
  indicators,
  onToggleIndicator,
}: IndicatorsButtonArrayProps) {
  const intl = useIntl();
  const theme = useTheme();
  const hasSelected = indicators.some((indicator) => {
    return selectedIndicators.some(({ objectId }) => objectId === indicator.objectId);
  });

  return (
    <div
      className={classNames(
        "relative",
        "-mx-6 flex gap-2 gap-y-2.5 overflow-x-auto border-y border-slate-300/40 bg-slate-50/80 px-4 py-2 dark:-mx-[18px] sm:flex-wrap sm:py-3",
        "dark:border-slate-950 dark:bg-stone-700/30",
        "scrollbar-none",
        className,
      )}
    >
      {indicators.map((indicator) => {
        const documentation = getIndicatorDocumentation(intl, indicator);
        const isSelected = selectedIndicators.some(({ objectId }) => objectId === indicator.objectId);
        return (
          <SimpleTextTooltip
            key={indicator.objectId}
            content={documentation.name}
            delayDuration={600}
            className="max-w-64"
          >
            <button
              type="button"
              disabled={!indicator.viewerCanSee}
              className={classNames(
                "flex flex-nowrap",
                "whitespace-nowrap rounded-full p-1.5 px-3 text-sm font-medium sm:px-2.5 sm:py-0.5",
                "leading-5 tracking-normal",
                "bg-slate-200/80 text-slate-600",
                "hover:bg-slate-600 hover:text-white",
                {
                  "dark:bg-sky-900/70 dark:text-sky-400 dark:hover:bg-sky-800 dark:hover:text-sky-300": !hasSelected,
                  "dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-teal-800 dark:hover:text-teal-300": hasSelected,
                },
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
                "focus-visible:border-transparent",
                "focus-visible:dark:ring-slate-600 focus-visible:dark:ring-offset-transparent",
                "disabled:cursor-not-allowed",
                {
                  "dark:bg-orange-600/20 dark:text-orange-200": isSelected,
                  "font-bold": isSelected,
                },
              )}
              style={((): CSSProperties => {
                if (!isSelected || theme === "dark") {
                  return {
                    backgroundColor: undefined,
                    color: undefined,
                  };
                }

                const indicatorIndex = selectedIndicators.findIndex(({ objectId }) => objectId === indicator.objectId);
                const themeColorSchema = getColorThemeForIndex(indicatorIndex);
                return {
                  backgroundColor: themeColorSchema.lineColor,
                  color: themeColorSchema.color,
                };
              })()}
              onClick={() => {
                onToggleIndicator(indicator, isSelected);
              }}
            >
              <span className="max-w-28 truncate"> {documentation.abbreviation || documentation.name}</span>
            </button>
          </SimpleTextTooltip>
        );
      })}
    </div>
  );
}
